import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import constants from '@core/utils/constants';
import walletSerializer from './serializer.js';
import {
  WALLET_STATUS,
  WALLET_POLLING_CONFIG,
  WALLET_PAYMENT_METHODS,
  FEDAPAY_TRANSACTION_STATUS
} from './constants.js';

/**
 * WalletController V3
 * Gère tous les événements wallet et les appels API
 * Intègre le PollingEngine pour le suivi des paiements
 */

// Store référence (sera initialisée au runtime)
let walletStore = null;

// Polling state pour les dépôts
const depositPollingState = {
  active: false,
  attempts: 0,
  timerId: null
};

// Flag pour éviter la double initialisation
let isInitialized = false;

// ============================================
// INITIALIZATION
// ============================================

/**
 * Initialise les écouteurs d'événements du controller
 */
function initController() {
  // Éviter la double initialisation
  if (isInitialized) {
    console.log('[WalletController] Already initialized, skipping');
    return;
  }

  console.log('[WalletController] Initializing wallet controller V3');
  isInitialized = true;

  // Get wallet store
  walletStore = getStore('wallet');
  if (!walletStore) {
    console.error('[WalletController] Wallet store not found');
    return;
  }

  // Enregistrer les écouteurs d'événements
  setupBalanceHandlers();
  setupDepositHandlers();
  setupWithdrawalHandlers();
  setupTransactionHandlers();
  setupAccountsHandlers();

  // Garder la compatibilité avec l'ancien système
  setupLegacyHandlers();

  console.log('[WalletController] All event handlers registered successfully');
}

// ============================================
// BALANCE HANDLERS
// ============================================

function setupBalanceHandlers() {
  // Charger le solde
  bus.on(constants.EVENTS.WALLET.BALANCE_LOAD, loadBalance);

  // Support de l'ancien événement
  bus.on(constants.EVENTS.WALLET.REFRESH, loadBalance);
}

/**
 * Charge le solde du wallet depuis l'API
 */
async function loadBalance() {
  const requestId = `wallet-balance-${Date.now()}`;

  console.log('[WalletController] Loading wallet balance');

  try {
    // Notifier le store que le chargement commence
    walletStore.state.loading = true;
    walletStore.state.error = null;
    bus.emit(constants.EVENTS.WALLET.REFRESHING);

    // Préparer l'URL de la requête
    const userStore = getStore('user');
    let userId = userStore?.state?.current?.id;

    // Pour tests sans auth: utiliser un userId par défaut
    if (!userId) {
      userId = 'test-user-123';
      console.log('[WalletController] Using test userId for balance:', userId);
    }

    // Utiliser le nouveau endpoint V3 et remplacer le paramètre {userId}
    const url = constants.ENDPOINTS.WALLET.BALANCE.replace('{userId}', userId);

    // Émettre la requête réseau
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url,
      method: 'GET',
      data: {}
    });

    // Écouter la réponse (une seule fois)
    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        if (res.success || res.data?.statusCode) {
          const balanceData = walletSerializer.deserializeBalance(res.data);
          bus.emit(constants.EVENTS.WALLET.BALANCE_UPDATE, balanceData);
          bus.emit(constants.EVENTS.WALLET.REFRESHED, balanceData);
        } else {
          const error = walletSerializer.deserializeError(res.data);

          // Toast notification pour l'utilisateur
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Solde inaccessible',
            body: 'Impossible de charger votre solde. Veuillez réessayer.'
          });

          bus.emit(constants.EVENTS.WALLET.BALANCE_ERROR, error);
          bus.emit(constants.EVENTS.WALLET.REFRESH_ERROR, error);
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[WalletController] Error loading balance:', error);
    walletStore.state.loading = false;
    const errorData = {
      message: error.message,
      code: 'WALLET_LOAD_ERROR'
    };

    // Toast notification pour l'utilisateur
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Solde inaccessible',
      body: 'Impossible de charger votre solde. Veuillez vérifier votre connexion.'
    });

    bus.emit(constants.EVENTS.WALLET.BALANCE_ERROR, errorData);
    bus.emit(constants.EVENTS.WALLET.REFRESH_ERROR, errorData);
  }
}

// ============================================
// DEPOSIT HANDLERS
// ============================================

function setupDepositHandlers() {
  bus.on(constants.EVENTS.WALLET.DEPOSIT_INITIATE, initiateDeposit);
  bus.on(constants.EVENTS.WALLET.DEPOSIT_POLLING_START, startDepositPolling);
  bus.on(constants.EVENTS.WALLET.DEPOSIT_POLLING_STOP, stopDepositPolling);
}

/**
 * Initialise un dépôt via FedaPay
 */
async function initiateDeposit(formData) {
  const requestId = `wallet-deposit-${Date.now()}`;

  console.log('[WalletController] Initiating deposit:', formData);

  try {
    // Reset du state de dépôt
    walletStore.actions.resetDeposit();
    walletStore.state.loading = true;
    walletStore.state.error = null;

    // Transformer les données du modal vers le format attendu par le serializer
    // Le modal envoie: { amount, provider, phoneNumber, currency }
    // Le serializer attend: { amount, phoneNumber, paymentMethod, currency, customer }

    const transformedData = {
      amount: formData?.amount,
      phoneNumber: formData?.phoneNumber,
      paymentMethod: formData?.provider || formData?.paymentMethod,
      currency: formData?.currency,
      customer: {
        firstname: formData?.customer?.firstname || '',
        lastname: formData?.customer?.lastname || '',
        email: formData?.customer?.email || '',
        phoneCountry: formData?.customer?.phoneCountry || 'bj'
      },
      userId: formData?.userId
    };

    // Sérialiser les données du formulaire avec les informations utilisateur
    const userStore = getStore('user');
    const userData = userStore?.state?.current || {};
    const payload = await walletSerializer.serializeFunding(transformedData, userData);

    console.log('[WalletController] Deposit payload:', payload);

    // Émettre la requête réseau
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url: constants.ENDPOINTS.WALLET.FUNDING,
      method: 'POST',
      data: payload
    });

    // Écouter la réponse
    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        walletStore.state.loading = false;

        if (res.success || res.data?.statusCode) {
          const depositData = walletSerializer.deserializeBroadcastResponse(res.data);

          // Mettre à jour le store
          walletStore.state.depositTransactionId = depositData.transactionId;

          console.log('[WalletController] Deposit initiated:', depositData);

          // Toast notification pour informer l'utilisateur
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'info',
            title: 'Paiement initié',
            body: 'Votre paiement a été initié. Veuillez valider sur votre téléphone puis vérifier votre solde.'
          });
        } else {
          const error = walletSerializer.deserializeError(res.data);
          walletStore.state.depositError = error.message;

          // Toast notification pour l'utilisateur
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Dépôt échoué',
            body: error.message || 'Une erreur est survenue lors de l\'initialisation du dépôt. Veuillez réessayer.'
          });

          bus.emit(constants.EVENTS.WALLET.DEPOSIT_ERROR, error);
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[WalletController] Error initiating deposit:', error);
    walletStore.state.loading = false;
    const errorData = {
      error: error.message,
      code: 'DEPOSIT_INIT_ERROR'
    };
    walletStore.state.depositError = error.message;

    // Toast notification pour l'utilisateur
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur de dépôt',
      body: 'Une erreur est survenue. Veuillez vérifier votre connexion et réessayer.'
    });

    bus.emit(constants.EVENTS.WALLET.DEPOSIT_ERROR, errorData);
  }
}

/**
 * Démarre le polling du statut de dépôt
 * Utilise PollingEngine pour le suivi automatique
 */
function startDepositPolling({ transactionId, fedaPayTransactionId }) {
  console.log('[WalletController] Starting deposit polling:', {
    transactionId,
    fedaPayTransactionId
  });

  // Stop existing polling if any
  stopDepositPolling();

  // Reset polling state
  depositPollingState.active = true;
  depositPollingState.attempts = 0;

  walletStore.state.depositPolling = true;
  walletStore.state.depositPollingAttempts = 0;

  // Start polling loop
  pollDepositStatus(transactionId, fedaPayTransactionId);
}

/**
 * Fonction récursive de polling du statut de dépôt
 */
function pollDepositStatus(transactionId, fedaPayTransactionId) {
  if (!depositPollingState.active) {
    console.log('[WalletController] Polling stopped');
    return;
  }

  const maxAttempts = WALLET_POLLING_CONFIG.MAX_ATTEMPTS;
  const interval = WALLET_POLLING_CONFIG.INTERVAL_MS;

  // Vérifier si on a atteint la limite
  if (depositPollingState.attempts >= maxAttempts) {
    console.error('[WalletController] Deposit polling timeout');
    stopDepositPolling();

    // Toast notification pour timeout
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Délai de paiement dépassé',
      body: 'Le paiement n\'a pas été validé à temps. Veuillez réessayer.'
    });

    bus.emit(constants.EVENTS.WALLET.DEPOSIT_ERROR, {
      error: 'Délai de paiement dépassé. Veuillez réessayer.',
      code: 'PAYMENT_TIMEOUT'
    });
    return;
  }

  depositPollingState.attempts++;
  walletStore.state.depositPollingAttempts = depositPollingState.attempts;

  console.log(
    `[WalletController] Polling attempt ${depositPollingState.attempts}/${maxAttempts}`
  );

  // Effectuer la requête de polling
  const requestId = `wallet-deposit-poll-${Date.now()}`;
  const url = constants.ENDPOINTS.WALLET.DEPOSIT_STATUS.replace(
    '{transactionId}',
    transactionId
  );

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: requestId,
    url,
    method: 'GET',
    data: {}
  });

  // Handler pour la réponse
  const responseHandler = (res) => {
    if (res.id === requestId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

      if (res.success || res.data?.statusCode) {
        const statusData = walletSerializer.deserializePollingStatus(res.data);

        console.log('[WalletController] Polling status:', statusData);

        // Émettre l'événement de mise à jour
        bus.emit(constants.EVENTS.WALLET.DEPOSIT_POLLING_UPDATE, {
          transactionId,
          status: statusData.status,
          fedaPayStatus: statusData.fedaPayStatus,
          completed: statusData.completed,
          attempts: depositPollingState.attempts,
          newBalance: statusData.newBalance
        });

        // Si complété, arrêter le polling
        if (statusData.completed) {
          console.log(
            '[WalletController] Deposit completed:',
            statusData.status
          );

          if (statusData.status === WALLET_STATUS.COMPLETED) {
            // Toast notification pour succès
            bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
              type: 'success',
              title: 'Paiement réussi',
              body: `Votre dépôt de ${statusData.newBalance || ''} FCFA a été effectué avec succès.`
            });

            bus.emit(constants.EVENTS.WALLET.DEPOSIT_SUCCESS, statusData);
          } else {
            // Toast notification pour échec
            bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
              type: 'error',
              title: 'Paiement échoué',
              body: 'Le paiement n\'a pas pu être complété. Veuillez réessayer.'
            });

            bus.emit(constants.EVENTS.WALLET.DEPOSIT_ERROR, {
              error: 'Paiement échoué',
              code: 'PAYMENT_FAILED',
              details: statusData
            });
          }

          stopDepositPolling();
          return;
        }

        // Continuer le polling
        if (depositPollingState.active) {
          // Backoff exponentiel optionnel
          const backoffInterval = Math.min(
            interval *
              Math.pow(
                WALLET_POLLING_CONFIG.BACKOFF_MULTIPLIER,
                Math.floor(depositPollingState.attempts / 5)
              ),
            WALLET_POLLING_CONFIG.MAX_INTERVAL_MS
          );

          depositPollingState.timerId = setTimeout(() => {
            pollDepositStatus(transactionId, fedaPayTransactionId);
          }, backoffInterval);
        }
      } else {
        // Erreur de polling - continuer avec retry
        console.error('[WalletController] Polling error, retrying...');
        if (depositPollingState.active) {
          depositPollingState.timerId = setTimeout(() => {
            pollDepositStatus(transactionId, fedaPayTransactionId);
          }, interval);
        }
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
}

/**
 * Arrête le polling de dépôt
 */
function stopDepositPolling() {
  console.log('[WalletController] Stopping deposit polling');

  depositPollingState.active = false;

  if (depositPollingState.timerId) {
    clearTimeout(depositPollingState.timerId);
    depositPollingState.timerId = null;
  }

  walletStore.state.depositPolling = false;
}

// ============================================
// WITHDRAWAL HANDLERS
// ============================================

function setupWithdrawalHandlers() {
  bus.on(constants.EVENTS.WALLET.WITHDRAWAL_INITIATE, initiateWithdrawal);
}

/**
 * Initialise un retrait via FedaPay Payout
 */
async function initiateWithdrawal(formData) {
  const requestId = `wallet-withdrawal-${Date.now()}`;

  console.log('[WalletController] Initiating withdrawal:', formData);

  try {
    // Reset du state de retrait
    walletStore.actions.resetWithdrawal();
    walletStore.state.loading = true;
    walletStore.state.error = null;

    // Transformer les données du modal vers le format attendu par le serializer
    // Le modal envoie: { amount, provider, phoneNumber, currency }
    // Le serializer attend: { amount, phoneNumber, paymentMethod, currency, customer, reason }
    const transformedData = {
      amount: formData?.amount,
      phoneNumber: formData?.phoneNumber,
      paymentMethod: formData?.provider || formData?.paymentMethod,
      currency: formData?.currency,
      reason: formData?.reason || 'Retrait',
      customer: {
        firstname: formData?.customer?.firstname || '',
        lastname: formData?.customer?.lastname || '',
        email: formData?.customer?.email || '',
        phoneCountry: formData?.customer?.phoneCountry || 'bj'
      },
      userId: formData?.userId
    };

    // Sérialiser les données du formulaire avec les informations utilisateur
    const userStore = getStore('user');
    const userData = userStore?.state?.current || {};
    const payload = await walletSerializer.serializeWithdrawal(transformedData, userData);

    console.log('[WalletController] Withdrawal payload:', payload);

    // Émettre la requête réseau
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url: constants.ENDPOINTS.WALLET.WITHDRAWAL,
      method: 'POST',
      data: payload
    });

    // Écouter la réponse
    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        walletStore.state.loading = false;

        if (res.success || res.data?.statusCode) {
          const withdrawalData = walletSerializer.deserializeBroadcastResponse(
            res.data
          );

          // Mettre à jour le store
          walletStore.state.withdrawalTransactionId =
            withdrawalData.transactionId;

          console.log(
            '[WalletController] Withdrawal initiated:',
            withdrawalData
          );

          // Toast notification pour informer l'utilisateur
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Retrait en cours',
            body: 'Votre demande de retrait a été enregistrée et est en cours de traitement.'
          });

          // Émettre l'événement de succès
          bus.emit(constants.EVENTS.WALLET.WITHDRAWAL_SUCCESS, withdrawalData);
        } else {
          const error = walletSerializer.deserializeError(res.data);
          walletStore.state.withdrawalError = error.message;

          // Toast notification pour l'utilisateur
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Retrait échoué',
            body: error.message || 'Une erreur est survenue lors du retrait. Veuillez réessayer.'
          });

          bus.emit(constants.EVENTS.WALLET.WITHDRAWAL_ERROR, error);
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[WalletController] Error initiating withdrawal:', error);
    walletStore.state.loading = false;
    const errorData = {
      error: error.message,
      code: 'WITHDRAWAL_INIT_ERROR'
    };
    walletStore.state.withdrawalError = error.message;

    // Toast notification pour l'utilisateur
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur de retrait',
      body: 'Une erreur est survenue. Veuillez vérifier votre connexion et réessayer.'
    });

    bus.emit(constants.EVENTS.WALLET.WITHDRAWAL_ERROR, errorData);
  }
}

// ============================================
// TRANSACTION HANDLERS
// ============================================

function setupTransactionHandlers() {
  bus.on(constants.EVENTS.WALLET.TRANSACTIONS_LOAD, loadTransactions);
}

/**
 * Charge l'historique des transactions
 */
async function loadTransactions(params = {}) {
  const requestId = `wallet-transactions-${Date.now()}`;

  console.log('[WalletController] Loading transactions:', params);

  try {
    walletStore.state.loading = true;
    walletStore.state.error = null;

    // Construire l'URL avec les query params
    const queryParams = walletSerializer.serializeTransactionQuery(params);

    // Ajouter userId (nécessaire pour l'API)
    const userStore = getStore('user');
    let userId = userStore?.state?.current?.id;

    // Pour tests sans auth: utiliser un userId par défaut
    if (!userId) {
      userId = 'test-user-123';
      console.log('[WalletController] Using test userId:', userId);
    }

    queryParams.userId = userId;

    const queryString = new URLSearchParams(queryParams).toString();
    const url = queryString
      ? `${constants.ENDPOINTS.WALLET.TRANSACTIONS}&${queryString}`
      : `${constants.ENDPOINTS.WALLET.TRANSACTIONS}&userId=${userId}`;

    console.log('[WalletController] Transactions URL:', url);

    // Émettre la requête réseau
    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url,
      method: 'GET',
      data: {}
    });

    // Écouter la réponse
    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        if (res.success || res.data?.statusCode) {
          const transactionsData = walletSerializer.deserializeTransactions(
            res.data
          );
          bus.emit(
            constants.EVENTS.WALLET.TRANSACTIONS_UPDATE,
            transactionsData
          );
          console.log(
            '[WalletController] Transactions loaded:',
            transactionsData.transactions.length
          );
        } else {
          const error = walletSerializer.deserializeError(res.data);

          // Toast notification pour l'utilisateur
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Transactions inaccessible',
            body: 'Impossible de charger vos transactions. Veuillez réessayer.'
          });

          bus.emit(constants.EVENTS.WALLET.TRANSACTIONS_ERROR, error);
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[WalletController] Error loading transactions:', error);
    walletStore.state.loading = false;
    const errorData = {
      message: error.message,
      code: 'TRANSACTIONS_LOAD_ERROR'
    };

    // Toast notification pour l'utilisateur
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Transactions inaccessible',
      body: 'Impossible de charger vos transactions. Veuillez vérifier votre connexion.'
    });

    bus.emit(constants.EVENTS.WALLET.TRANSACTIONS_ERROR, errorData);
  }
}

// ============================================
// ACCOUNTS HANDLERS
// ============================================

function setupAccountsHandlers() {
  bus.on(constants.EVENTS.WALLET.ACCOUNTS_LOAD, loadAccounts);
  bus.on(constants.EVENTS.WALLET.ACCOUNT_CREATE, createAccount);
}

/**
 * Charge la liste des comptes de retrait de l'utilisateur
 */
async function loadAccounts() {
  const requestId = `wallet-accounts-${Date.now()}`;

  console.log('[WalletController] Loading withdrawal accounts');

  try {
    walletStore.state.accountsLoading = true;
    walletStore.state.accountsError = null;

    const userStore = getStore('user');
    let userId = userStore?.state?.current?.id;

    if (!userId) {
      userId = 'test-user-123';
      console.log('[WalletController] Using test userId for accounts:', userId);
    }

    const url = `${constants.ENDPOINTS.WALLET.ACCOUNTS_LIST}&userId=${userId}`;

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url,
      method: 'GET',
      data: {}
    });

    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        if (res.success || res.data?.statusCode === 200) {
          const accountsData = walletSerializer.deserializeAccounts(res.data);
          bus.emit(constants.EVENTS.WALLET.ACCOUNTS_UPDATE, accountsData);
          console.log('[WalletController] Accounts loaded:', accountsData.accounts.length);
        } else {
          const error = walletSerializer.deserializeError(res.data);
          bus.emit(constants.EVENTS.WALLET.ACCOUNTS_ERROR, error);
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[WalletController] Error loading accounts:', error);
    walletStore.state.accountsLoading = false;
    bus.emit(constants.EVENTS.WALLET.ACCOUNTS_ERROR, {
      message: error.message,
      code: 'ACCOUNTS_LOAD_ERROR'
    });
  }
}

/**
 * Enregistre un nouveau compte de retrait auprès de l'API MonRapidoo
 */
async function createAccount(formData) {
  const requestId = `wallet-account-create-${Date.now()}`;

  console.log('[WalletController] Creating withdrawal account:', formData);

  try {
    walletStore.state.accountsLoading = true;
    walletStore.state.accountsError = null;

    const userStore = getStore('user');
    const userData = userStore?.state?.current || {};
    const payload = walletSerializer.serializeAccount(formData, userData);

    console.log('[WalletController] Account create payload:', payload);

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url: constants.ENDPOINTS.WALLET.ACCOUNT_CREATE,
      method: 'POST',
      data: payload
    });

    const responseHandler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, responseHandler);

        walletStore.state.accountsLoading = false;

        if (res.success || res.data?.statusCode === 201 || res.data?.statusCode === 200) {
          const rawAccount = res.data?.data || res.data;
          const account = walletSerializer.deserializeAccount(rawAccount);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'success',
            title: 'Compte ajouté',
            body: 'Votre compte de retrait a été enregistré avec succès.'
          });

          bus.emit(constants.EVENTS.WALLET.ACCOUNT_CREATED, { account });
          // Recharger la liste des comptes pour s'assurer de la synchronisation
          loadAccounts();
        } else {
          const error = walletSerializer.deserializeError(res.data, "accountCreation");
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            type: 'error',
            title: 'Erreur d’enregistrement',
            body: error.message || "Impossible d'enregistrer le compte de retrait."
          });
          bus.emit(constants.EVENTS.WALLET.ACCOUNT_CREATE_ERROR, error);
        }
      }
    };

    bus.on(constants.EVENTS.NETWORK.RESPONSE, responseHandler);
  } catch (error) {
    console.error('[WalletController] Error creating account:', error);
    walletStore.state.accountsLoading = false;
    const errorData = {
      message: error.message,
      code: 'ACCOUNT_CREATE_ERROR'
    };
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      type: 'error',
      title: 'Erreur d’enregistrement',
      body: error.message || "Impossible d'enregistrer le compte de retrait."
    });
    bus.emit(constants.EVENTS.WALLET.ACCOUNT_CREATE_ERROR, errorData);
  }
}

// ============================================
// LEGACY HANDLERS (Compatibilité)
// ============================================

function setupLegacyHandlers() {
  // L'ancien système utilise déjà des handlers dans le controller existant
  // On garde la compatibilité en écoutant les mêmes événements
  console.log('[WalletController] Legacy handlers setup completed');
}

// ============================================
// EXPORT
// ============================================

// Auto-initialiser le controller quand le store est disponible
bus.on(constants.EVENTS.APP.READY, () => {
  setTimeout(() => {
    initController();
  }, 100);
});

// Export des fonctions pour accès externe si nécessaire
export const walletController = {
  init: initController,
  loadBalance,
  initiateDeposit,
  startDepositPolling,
  stopDepositPolling,
  initiateWithdrawal,
  loadTransactions
};

export default walletController;
